import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

/**
 * MER-167 v1: app-wide toast notifications.
 *
 * Mount `<ToastProvider>` once at the app root. Components fire
 * notifications via `useToast()`. Toasts stack bottom-right, brand-
 * styled per type, auto-dismiss for non-sticky variants.
 *
 * v1 trigger surfaces:
 * - RED FLAG poller (useFlagStream)
 * - Freshness severe transition (in MER-152 useFreshness)
 *
 * Deferred:
 * - Sync success/failure: needs the MER-151 scheduler-runs table.
 * - Manifest reload: needs a version-polling hook.
 */

export type ToastType = "info" | "success" | "warning" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  body?: string;
  /** Sticky toasts stay until dismissed; non-sticky auto-dismiss after 4s. */
  sticky?: boolean;
  action?: ToastAction;
  /** Dedupe key so the same warning isn't fired N times. */
  dedupeKey?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 4000;

export function ToastProvider(props: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Keep a ref so the auto-dismiss timeout closures don't capture
  // stale state during rapid pushes.
  const seenDedupe = useRef<Set<string>>(new Set());

  const push = useCallback((toast: Omit<Toast, "id">): string => {
    if (toast.dedupeKey && seenDedupe.current.has(toast.dedupeKey)) return "";
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (toast.dedupeKey) seenDedupe.current.add(toast.dedupeKey);
    setToasts((prev) => [...prev, { ...toast, id }]);
    if (!toast.sticky) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, AUTO_DISMISS_MS);
    }
    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {props.children}
      <ToastHost />
    </ToastContext.Provider>
  );
}

export function useToast(): Pick<ToastContextValue, "push" | "dismiss"> {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Outside a provider: no-op. Lets components that mount in tests
    // (without the provider) call useToast() without crashing.
    return {
      push: () => "",
      dismiss: () => undefined,
    };
  }
  return { push: ctx.push, dismiss: ctx.dismiss };
}

const TYPE_PALETTE: Record<ToastType, { bg: string; fg: string; border: string; icon: string }> = {
  info: {
    bg: "rgba(38, 168, 159, 0.06)",
    fg: "var(--brand-deep)",
    border: "var(--brand-line)",
    icon: "ⓘ",
  },
  success: {
    bg: "rgba(39, 174, 96, 0.06)",
    fg: "rgb(20, 130, 70)",
    border: "rgba(39, 174, 96, 0.25)",
    icon: "✓",
  },
  warning: {
    bg: "rgba(230, 126, 34, 0.08)",
    fg: "rgb(180, 90, 20)",
    border: "rgba(230, 126, 34, 0.3)",
    icon: "⚠",
  },
  error: {
    bg: "rgba(192, 57, 43, 0.08)",
    fg: "var(--flag-red)",
    border: "rgba(192, 57, 43, 0.3)",
    icon: "✕",
  },
};

const HOST_STYLE: CSSProperties = {
  position: "fixed",
  right: 24,
  bottom: 24,
  display: "grid",
  gap: 10,
  zIndex: 1000,
  maxWidth: 380,
  pointerEvents: "none",
};

function ToastHost() {
  const ctx = useContext(ToastContext);
  if (!ctx || ctx.toasts.length === 0) return null;
  return (
    <div style={HOST_STYLE} aria-live="polite" aria-atomic="false">
      {ctx.toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => ctx.dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard(props: { toast: Toast; onDismiss: () => void }) {
  const { toast, onDismiss } = props;
  const palette = TYPE_PALETTE[toast.type];
  return (
    <article
      role={toast.type === "error" || toast.type === "warning" ? "alert" : "status"}
      style={{
        pointerEvents: "auto",
        background: "var(--panel)",
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        padding: "12px 14px",
        boxShadow: "var(--shadow-card)",
        display: "grid",
        gridTemplateColumns: "20px 1fr auto",
        alignItems: "start",
        gap: 10,
        fontFamily: "var(--font-stack)",
      }}
    >
      <span
        aria-hidden
        style={{
          color: palette.fg,
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1.2,
          background: palette.bg,
          borderRadius: 4,
          width: 20,
          height: 20,
          display: "grid",
          placeItems: "center",
        }}
      >
        {palette.icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{toast.title}</div>
        {toast.body ? (
          <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2, lineHeight: 1.4 }}>
            {toast.body}
          </div>
        ) : null}
        {toast.action ? (
          <button
            type="button"
            onClick={toast.action.onClick}
            style={{
              marginTop: 8,
              font: "inherit",
              fontSize: 12,
              fontWeight: 600,
              color: palette.fg,
              background: "transparent",
              border: `1px solid ${palette.border}`,
              borderRadius: 4,
              padding: "4px 10px",
              cursor: "pointer",
            }}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        style={{
          font: "inherit",
          fontSize: 14,
          color: "var(--text-muted)",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </article>
  );
}
